"use client";

import React, { useContext, useCallback, useMemo } from "react";
import { EyeOff, Eye, Settings2, RotateCcw, ArrowUp, ArrowDown, X, Check } from "lucide-react";
import { useGlobalModalStack } from "@wtc/ui";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { ColumnManagerContext } from "@/lib/column-manager";
import { MANAGE_TABLE_COLUMNS_MODAL_ID } from "@/components/shared/SimpleColumnManager/constants";
import { useSimpleTableContext } from "../../contexts/SimpleTableContext";
import type { BaseEntity } from "../../types";

interface ColumnHeaderContextMenuProps {
  /** Column id (equal to the table field id) this header renders. */
  fieldId: string;
  /** Human-readable label, used in the "Hide <label>" item. */
  label: string;
  /** Whether this column is sortable (drives the sort section). */
  sortable: boolean;
  /** The header cell — cloned as the right-click trigger. */
  children: React.ReactElement;
}

type SortDirection = "asc" | "desc";

/**
 * Wraps a column header so right-clicking it exposes quick sort + column
 * controls (sort asc/desc, hide this column, re-show a hidden column, open the
 * full manager) without opening the modal first.
 *
 * Sort state is read from SimpleTableContext (always present for a header).
 * Column-manager state is optional: outside a ColumnManagerProvider only the
 * sort section shows, and if neither sort nor column management applies the
 * header renders untouched. `fieldId` equals the ColumnManager
 * `SimpleColumn.id` (see createColumnsFromConfig), so `toggleColumn(fieldId)`
 * targets this column.
 */
export function ColumnHeaderContextMenu<TData extends BaseEntity>({
  fieldId,
  label,
  sortable,
  children,
}: ColumnHeaderContextMenuProps) {
  const columnManager = useContext(ColumnManagerContext);
  const { openModal } = useGlobalModalStack();
  const { config, state, callbacks } = useSimpleTableContext<TData>();

  const isMultiSort = config.features?.multiColumnSort === true;

  // Current sort direction for THIS field (single- or multi-column mode).
  const activeDirection: SortDirection | null = useMemo(() => {
    if (isMultiSort) {
      return state.sortColumns.find((c) => c.field === fieldId)?.direction ?? null;
    }
    return state.sortField === fieldId ? state.sortDirection : null;
  }, [isMultiSort, state.sortColumns, state.sortField, state.sortDirection, fieldId]);

  const applySort = useCallback(
    (direction: SortDirection) => {
      if (isMultiSort) {
        // Upsert this field's sort, preserving priority for existing entries.
        state.setSortColumns(
          (() => {
            const existing = state.sortColumns.find((c) => c.field === fieldId);
            if (existing) {
              return state.sortColumns.map((c) =>
                c.field === fieldId ? { ...c, direction } : c
              );
            }
            const maxPriority = state.sortColumns.reduce(
              (max, c) => Math.max(max, c.priority),
              0
            );
            return [...state.sortColumns, { field: fieldId, direction, priority: maxPriority + 1 }];
          })()
        );
      } else {
        state.setSortField(fieldId);
        state.setSortDirection(direction);
      }
    },
    [isMultiSort, state, fieldId]
  );

  const clearSort = useCallback(() => {
    if (isMultiSort) {
      callbacks.removeSortColumn(fieldId);
    } else {
      // Only clear when this field is the active single sort.
      if (state.sortField === fieldId) {
        state.setSortField("");
      }
    }
  }, [isMultiSort, callbacks, state, fieldId]);

  // Hidden columns worth re-showing exclude form-only and the synthetic actions
  // column, matching what the modal offers as toggleable.
  const hiddenColumns = useMemo(
    () =>
      (columnManager?.hiddenColumns ?? []).filter(
        (col) => !col.formOnly && col.id !== "actions"
      ),
    [columnManager?.hiddenColumns]
  );

  // Guard against hiding the last visible data column, which would leave the
  // table with no content columns and no obvious way back except the modal.
  const visibleDataColumnCount = useMemo(
    () =>
      (columnManager?.visibleColumns ?? []).filter(
        (col) => !col.formOnly && col.id !== "actions"
      ).length,
    [columnManager?.visibleColumns]
  );

  const handleHide = useCallback(() => {
    columnManager?.toggleColumn(fieldId);
  }, [columnManager, fieldId]);

  const handleShow = useCallback(
    (id: string) => columnManager?.toggleColumn(id),
    [columnManager]
  );

  const handleReset = useCallback(() => {
    columnManager?.resetToDefaults();
  }, [columnManager]);

  const handleManage = useCallback(() => {
    openModal(MANAGE_TABLE_COLUMNS_MODAL_ID);
  }, [openModal]);

  const hasColumnSection = Boolean(columnManager);

  // Nothing to offer → render the header untouched.
  if (!sortable && !hasColumnSection) {
    return children;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        {sortable && (
          <>
            <ContextMenuItem onSelect={() => applySort("asc")}>
              <ArrowUp className="mr-2 h-4 w-4" />
              Sort ascending
              {activeDirection === "asc" && <Check className="ml-auto h-4 w-4" />}
            </ContextMenuItem>
            <ContextMenuItem onSelect={() => applySort("desc")}>
              <ArrowDown className="mr-2 h-4 w-4" />
              Sort descending
              {activeDirection === "desc" && <Check className="ml-auto h-4 w-4" />}
            </ContextMenuItem>
            <ContextMenuItem disabled={activeDirection === null} onSelect={clearSort}>
              <X className="mr-2 h-4 w-4" />
              Clear sort
            </ContextMenuItem>
            {hasColumnSection && <ContextMenuSeparator />}
          </>
        )}

        {hasColumnSection && (
          <>
            <ContextMenuItem
              disabled={visibleDataColumnCount <= 1}
              onSelect={handleHide}
            >
              <EyeOff className="mr-2 h-4 w-4" />
              Hide &ldquo;{label}&rdquo;
            </ContextMenuItem>

            <ContextMenuSub>
              <ContextMenuSubTrigger disabled={hiddenColumns.length === 0}>
                <Eye className="mr-2 h-4 w-4" />
                Show column
              </ContextMenuSubTrigger>
              <ContextMenuSubContent className="max-h-72 w-56 overflow-y-auto">
                {hiddenColumns.length === 0 ? (
                  <ContextMenuLabel className="text-xs font-normal text-muted-foreground">
                    No hidden columns
                  </ContextMenuLabel>
                ) : (
                  hiddenColumns.map((col) => (
                    <ContextMenuItem key={col.id} onSelect={() => handleShow(col.id)}>
                      {col.label}
                    </ContextMenuItem>
                  ))
                )}
              </ContextMenuSubContent>
            </ContextMenuSub>

            <ContextMenuSeparator />

            <ContextMenuItem onSelect={handleManage}>
              <Settings2 className="mr-2 h-4 w-4" />
              Manage columns…
            </ContextMenuItem>
            <ContextMenuItem onSelect={handleReset}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset to defaults
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
